import { Locator, LocatorLocations, LocatorText } from '@readium/shared';

/**
 * Creates a Readium Locator from epubR2Location data for navigation.
 * Applies workaround for @readium/navigator bug that ignores progression === 0.
 * @param {Object} epubR2Location - Location data with href, type, and locations
 * @returns {Locator} Readium Locator instance ready for navigation
 */
export function createR2Locator(epubR2Location) {
    const locatorData = { ...epubR2Location };

    // Workaround: @readium/navigator ignores progression === 0
    // Bug in EpubNavigator.ts:758 - condition `progression > 0` excludes 0
    if (locatorData.locations?.progression === 0) {
        locatorData.locations = { ...locatorData.locations, progression: 0.0001 };
    }

    // Rebuild locations through deserialize so the HTML extensions (getCssSelector/htmlId)
    // and the otherLocations Map are present. Building from a plain object would leave go()
    // unable to use a cssSelector/id anchor, silently restoring by progression only — losing
    // the precise-element anchoring that bookmarks/highlights serialize into epubR2Location.
    const locations = LocatorLocations.deserialize(locatorData.locations);

    // text may be a plain highlight string (legacy notes/highlights) or a serialized RWPM
    // text object { before, highlight, after } (bookmarks from locator.serialize()). Either
    // form lets go() locate the text in the resource.
    let text;
    if (typeof locatorData.text === 'string') {
        text = new LocatorText({ highlight: locatorData.text });
    } else if (locatorData.text) {
        text = LocatorText.deserialize(locatorData.text);
    }

    return new Locator({
        href: locatorData.href,
        type: locatorData.type,
        title: locatorData.title,
        locations,
        text,
    });
}

/**
 * Rebuilds a restored reading position into a real Locator.
 *
 * The host sends it as plain JSON and `parseLastPosition` assembles one by hand, so
 * neither carries `locations.fragments`. Navigator 2.6.0 routes every `_pong` through
 * `Timeline.locate()`, which reads `fragments` unguarded — leaving it undefined aborts
 * `nav.load()`. `LocatorLocations` defaults it to an empty array.
 *
 * @param {Object|Locator} [savedPosition] - Restored position, in either shape
 * @returns {Locator|undefined} The same value when already a Locator, or a rebuilt one
 */
export function toInitialLocator(savedPosition) {
    if (!savedPosition || savedPosition instanceof Locator) return savedPosition;

    return new Locator({
        href: savedPosition.href,
        type: savedPosition.type,
        title: savedPosition.title,
        locations: LocatorLocations.deserialize(savedPosition.locations) ?? new LocatorLocations({}),
        text: savedPosition.text ? LocatorText.deserialize(savedPosition.text) : undefined,
    });
}

export function parseCFI(cfi) {
    const parts = cfi.split('/').filter(p => p);

    return {
        spineIndex: parseInt(parts[0]) - 1,
        elementPath: parts.slice(1),
        textOffset: parts[parts.length - 1].split(':'),
    };
}
